import React, { useEffect, useRef } from "react";
import { View, StyleSheet, Animated, Easing } from "react-native";
import Svg, {
  Defs,
  RadialGradient,
  LinearGradient,
  Stop,
  Rect,
  Pattern,
  Path,
  G,
  ClipPath,
} from "react-native-svg";
import {
  EMBLEM_PATHS,
  PIECE_SVGS,
  PieceKey,
} from "../clubPieces";
import { PIECE_WHITE, PIECE_BLACK } from "../badgeOptions";

// react-native-svg's web build does not export <SvgXml>, so we parse the
// bundled piece XML ourselves and render each <path> with the <Path> primitive
// (works identically on native + web). Piece SVGs are simple <path d… fill…>.
type ParsedPath = { d: string; fill: string; stroke?: string; strokeWidth?: number };
function parsePiecePaths(xml: string, fill: string): ParsedPath[] {
  const paths: ParsedPath[] = [];
  const re = /<path\b([^>]*?)\/?>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) {
    const attrs = m[1];
    const d = /\bd="([^"]*)"/.exec(attrs)?.[1];
    if (!d) continue;
    const rawFill = /\bfill="([^"]*)"/.exec(attrs)?.[1] ?? "";
    const strokeAttr = /\bstroke="([^"]*)"/.exec(attrs)?.[1];
    const swAttr = /\bstroke-width="([^"]*)"/.exec(attrs)?.[1];
    paths.push({
      d,
      fill: rawFill === "{{FILL}}" || rawFill === "" ? fill : rawFill,
      stroke: strokeAttr,
      strokeWidth: swAttr ? parseFloat(swAttr) : undefined,
    });
  }
  return paths;
}

// Reproduces the design prototype's chess-tile "crest": a glossy, beveled
// square with a subtle checkerboard texture and a chess-piece / emblem figure.
// Rendered entirely with react-native-svg so gradients, the checker pattern and
// the inset bevels match the web prototype (Caissa Clubs.dc.html crestStyle).

type Props = {
  piece: PieceKey;
  color: string; // base background color
  pieceColor?: "white" | "black";
  size: number;
  selected?: boolean;
  hovered?: boolean; // pointer is over the badge (web) — triggers place-bounce
  showCheck?: boolean; // draw the ✓ chip when selected (badge picker only)
  animated?: boolean; // enable hover/select motion (badge picker only)
  radius?: number;
};

// The crest is drawn on a 100x100 canvas and scaled to `size`.
const V = 100;

export default function ClubBadge({
  piece,
  color,
  pieceColor = "white",
  size,
  selected = false,
  hovered = false,
  showCheck = false,
  animated = false,
  radius,
}: Props) {
  const r = radius ?? size * 0.18; // design: 18px radius on ~100px tile
  const fig = pieceColor === "black" ? PIECE_BLACK : PIECE_WHITE;

  const emblem = EMBLEM_PATHS[piece];
  const pieceXml = PIECE_SVGS[piece];

  // Figure occupies ~62% of the tile (design: ~1.6em glyph on a 34px-font tile).
  const figSize = size * 0.62;

  // ---- Motion (design: ccPiecePlace place-bounce, ccBadgeFloat loop,
  // ccBadgeShine sweep). Values drive a transform on the whole crest. ----
  const place = useRef(new Animated.Value(1)).current; // 1 = rest (identity); bounce replays 0→1
  const floatV = useRef(new Animated.Value(0)).current; // continuous float
  const shine = useRef(new Animated.Value(0)).current; // shine sweep 0→1
  const floatLoop = useRef<Animated.CompositeAnimation | null>(null);
  const shineLoop = useRef<Animated.CompositeAnimation | null>(null);

  // Place-bounce (ccPiecePlace, cubic-bezier(.34,1.56,.64,1) ≈ back-out).
  const bounce = () => {
    place.setValue(0);
    Animated.timing(place, {
      toValue: 1,
      duration: 500,
      easing: Easing.bezier(0.34, 1.56, 0.64, 1),
      useNativeDriver: true,
    }).start();
  };

  // Trigger the bounce on hover-in (when not selected) and on becoming selected.
  useEffect(() => {
    if (animated && hovered && !selected) bounce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered]);

  useEffect(() => {
    if (!animated) return;
    if (selected) {
      bounce();
      // Gentle float loop (ccBadgeFloat 2.2s), starting after the bounce.
      floatLoop.current = Animated.loop(
        Animated.sequence([
          Animated.timing(floatV, { toValue: 1, duration: 1100, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
          Animated.timing(floatV, { toValue: 0, duration: 1100, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        ]),
      );
      const t = setTimeout(() => floatLoop.current?.start(), 500);
      // Shine sweep (ccBadgeShine 2.4s infinite).
      shineLoop.current = Animated.loop(
        Animated.timing(shine, { toValue: 1, duration: 2400, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      );
      shineLoop.current.start();
      return () => {
        clearTimeout(t);
        floatLoop.current?.stop();
        shineLoop.current?.stop();
        floatV.setValue(0);
        shine.setValue(0);
      };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, animated]);

  // ccPiecePlace: translateY -14→0 with a scaleY squash/stretch, ending at 1.
  const placeTranslate = place.interpolate({ inputRange: [0, 0.55, 0.75, 1], outputRange: [-size * 0.14, 0, 0, 0] });
  const placeScaleY = place.interpolate({ inputRange: [0, 0.55, 0.75, 1], outputRange: [0.86, 0.92, 1.05, 1] });
  const placeScaleX = place.interpolate({ inputRange: [0, 0.55, 0.75, 1], outputRange: [1, 1.08, 0.96, 1] });
  const floatTranslate = floatV.interpolate({ inputRange: [0, 1], outputRange: [0, -size * 0.03] });
  // Shine band sweeps from left off-screen to right off-screen.
  const shineTranslate = shine.interpolate({ inputRange: [0, 0.6, 1], outputRange: [-size * 1.2, size * 2.4, size * 2.4] });

  const animStyle = animated
    ? {
        transform: [
          { translateY: Animated.add(placeTranslate, floatTranslate) },
          { scaleX: placeScaleX },
          { scaleY: placeScaleY },
        ],
      }
    : undefined;

  return (
    <Animated.View style={[styles.shadowWrap, { width: size, height: size, borderRadius: r }, animStyle]}>
      {/* Tile + shine, clipped to the rounded square. */}
      <View
        style={{
          width: size,
          height: size,
          borderRadius: r,
          overflow: "hidden",
        }}
      >
        <Svg width={size} height={size} viewBox={`0 0 ${V} ${V}`}>
          <Defs>
            {/* base + gloss + shade + specular dot, matching the CSS stack */}
            <RadialGradient
              id="topGloss"
              cx="50%"
              cy="-12%"
              rx="60%"
              ry="80%"
            >
              <Stop offset="0" stopColor="#ffffff" stopOpacity="0.42" />
              <Stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
            </RadialGradient>
            <RadialGradient
              id="botShade"
              cx="50%"
              cy="122%"
              rx="80%"
              ry="72%"
            >
              <Stop offset="0" stopColor="#000000" stopOpacity="0.34" />
              <Stop offset="0.62" stopColor="#000000" stopOpacity="0" />
            </RadialGradient>
            {/* soft bottom inner shadow (design box-shadow inset 0 -6px 10px). */}
            <LinearGradient id="botInner" x1="0" y1="1" x2="0" y2="0.6">
              <Stop offset="0" stopColor="#000000" stopOpacity="0.36" />
              <Stop offset="1" stopColor="#000000" stopOpacity="0" />
            </LinearGradient>
            {/* top inner highlight (design inset 0 3px 8px rgba(255,255,255,.3)). */}
            <LinearGradient id="topInner" x1="0" y1="0" x2="0" y2="0.4">
              <Stop offset="0" stopColor="#ffffff" stopOpacity="0.30" />
              <Stop offset="1" stopColor="#ffffff" stopOpacity="0" />
            </LinearGradient>
            <LinearGradient id="sheen" x1="0" y1="0" x2="0.6" y2="1">
              <Stop offset="0" stopColor="#ffffff" stopOpacity="0.12" />
              <Stop offset="0.42" stopColor="#ffffff" stopOpacity="0" />
            </LinearGradient>
            {/* Two-tone checkerboard: white on one square, black on the alternate
                (design combines a light boardStyle grid + a 2-tone conic checker,
                deepening the green instead of only lightening it). ~11px squares. */}
            <Pattern
              id="checker"
              width="22"
              height="22"
              patternUnits="userSpaceOnUse"
            >
              <Rect x="0" y="0" width="11" height="11" fill="#ffffff" opacity="0.13" />
              <Rect x="11" y="0" width="11" height="11" fill="#000000" opacity="0.10" />
              <Rect x="0" y="11" width="11" height="11" fill="#000000" opacity="0.10" />
              <Rect x="11" y="11" width="11" height="11" fill="#ffffff" opacity="0.13" />
            </Pattern>
            <ClipPath id="tile">
              <Rect x="0" y="0" width={V} height={V} rx={(r / size) * V} />
            </ClipPath>
          </Defs>

          <G clipPath="url(#tile)">
            {/* base color */}
            <Rect x="0" y="0" width={V} height={V} fill={color} />
            {/* checkerboard texture */}
            <Rect x="0" y="0" width={V} height={V} fill="url(#checker)" />
            {/* bottom shade, top gloss, diagonal sheen */}
            <Rect x="0" y="0" width={V} height={V} fill="url(#botShade)" />
            <Rect x="0" y={V * 0.5} width={V} height={V * 0.5} fill="url(#botInner)" />
            <Rect x="0" y="0" width={V} height={V * 0.35} fill="url(#topInner)" />
            <Rect x="0" y="0" width={V} height={V} fill="url(#topGloss)" />
            <Rect x="0" y="0" width={V} height={V} fill="url(#sheen)" />
            {/* specular dot, top-left */}
            <Rect x="22" y="16" width="4" height="4" rx="2" fill="#ffffff" opacity="0.85" />

            {/* inset bevels (box-shadow insets) */}
            <Rect
              x="1"
              y="1"
              width={V - 2}
              height={V - 2}
              rx={(r / size) * V - 1}
              fill="none"
              stroke="#ffffff"
              strokeOpacity="0.45"
              strokeWidth="2"
            />
            <Rect
              x="3.5"
              y="3.5"
              width={V - 7}
              height={V - 7}
              rx={(r / size) * V - 3.5}
              fill="none"
              stroke="#000000"
              strokeOpacity="0.18"
              strokeWidth="3"
            />
          </G>

          {/* selected ring (design: 0 0 0 3px #FACF47) */}
          {selected ? (
            <Rect
              x="1.5"
              y="1.5"
              width={V - 3}
              height={V - 3}
              rx={(r / size) * V - 1.5}
              fill="none"
              stroke="#FACF47"
              strokeWidth="3"
            />
          ) : null}
        </Svg>

        {/* Shine sweep (design ccBadgeShine): a diagonal gloss band that slides
            across the tile while selected. Clipped to the tile by the parent's
            overflow:hidden + border radius. */}
        {animated && selected ? (
          <Animated.View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: size * 0.35,
              transform: [{ translateX: shineTranslate }, { skewX: "-18deg" }],
              // @ts-ignore web gradient; on native the flat tint is a close read
              backgroundImage:
                "linear-gradient(90deg,transparent,rgba(255,255,255,0.55),transparent)",
              backgroundColor: "rgba(255,255,255,0.12)",
            }}
          />
        ) : null}
      </View>

      {/* Piece figure, centered, with its own drop shadow. */}
      <View style={[StyleSheet.absoluteFill, styles.center]} pointerEvents="none">
        {emblem ? (
          <Svg width={figSize} height={figSize} viewBox="0 0 48 48">
            <Path d={emblem} fill={fig} stroke="rgba(0,0,0,0.18)" strokeWidth={1} />
          </Svg>
        ) : pieceXml ? (
          <Svg width={figSize} height={figSize} viewBox="0 0 65 65">
            {parsePiecePaths(pieceXml, fig).map((p, i) => (
              <Path
                key={i}
                d={p.d}
                fill={p.fill}
                stroke={p.stroke}
                strokeWidth={p.strokeWidth}
              />
            ))}
          </Svg>
        ) : null}
      </View>

      {/* selected check chip (badge picker) */}
      {selected && showCheck ? (
        <View style={[styles.checkChip, { width: size * 0.18, height: size * 0.18, borderRadius: size * 0.09 }]}>
          <Svg width={size * 0.11} height={size * 0.11} viewBox="0 0 24 24">
            <Path
              d="M20 6 9 17l-5-5"
              fill="none"
              stroke="#4D3F37"
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      ) : null}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  shadowWrap: {
    shadowColor: "#4D3F37",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 4,
    // @ts-ignore web-only, harmless on native
    filter: "drop-shadow(0 4px 6px rgba(77,63,55,0.3))",
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  checkChip: {
    position: "absolute",
    top: "4%",
    right: "4%",
    backgroundColor: "#FACF47",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3,
  },
});
